// Shared modal for filling required KPI values before a card is marked
// done. Extracted from CardDetail so the Dashboard's handleComplete can
// gate on it too. Validation hardened: numeric, > 0, no blanks.
import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { X } from 'lucide-react'
import { parseKpiValue } from '../lib/kpi'

export default function CompletionKpiModal({ fields, initialValues, onClose, onSubmit, saving }) {
    const { t } = useTranslation()
    const [values, setValues] = useState(() =>
        fields.reduce((acc, f) => {
            acc[f.key] = initialValues?.[f.key] ?? ''
            return acc
        }, {}),
    )

    const fieldStates = fields.map((f) => ({ field: f, ...parseKpiValue(values[f.key]) }))
    const invalidFields = fieldStates.filter((s) => !s.ok)
    const allValid = invalidFields.length === 0

    return (
        <div className="modal-overlay" onClick={saving ? undefined : onClose}>
            <div className="modal app-modal cd-kpi-modal" onClick={(e) => e.stopPropagation()}>
                <div className="modal-header">
                    <h3 className="modal-title">{t('card.kpiModal.title')}</h3>
                    <button type="button" className="modal-close" onClick={onClose} disabled={saving}>
                        <X size={18} />
                    </button>
                </div>

                <div className="modal-body cd-kpi-modal-body">
                    <p className="cd-kpi-modal-copy">{t('card.kpiModal.intro')}</p>

                    <div className="cd-kpi-modal-fields">
                        {fields.map((f) => {
                            const state = fieldStates.find((s) => s.field.key === f.key)
                            const ppu = Number(f.points)
                            const hasPoints = Number.isFinite(ppu) && ppu !== 0
                            return (
                                <label key={f.key} className="cd-kpi-modal-field">
                                    <span>
                                        {f.label}
                                        {hasPoints ? (
                                            <span style={{ marginLeft: 6, fontSize: 11, color: 'var(--text-secondary)' }}>
                                                · {ppu} pt/u
                                            </span>
                                        ) : null}
                                    </span>
                                    <input
                                        className="inline-input"
                                        type="number"
                                        min="1"
                                        step="1"
                                        value={values[f.key] ?? ''}
                                        onChange={(e) =>
                                            setValues((prev) => ({ ...prev, [f.key]: e.target.value }))
                                        }
                                        placeholder={t('card.kpiModal.fieldPlaceholder', { label: f.label.toLowerCase() })}
                                        disabled={saving}
                                        autoFocus={fields[0].key === f.key}
                                    />
                                    {!state.ok && String(values[f.key] ?? '').trim() && (
                                        <span className="cd-kpi-modal-fielderror">
                                            {t('card.kpiModal.invalidNumber', { defaultValue: 'Trebuie să fie un număr mai mare ca 0' })}
                                        </span>
                                    )}
                                </label>
                            )
                        })}
                    </div>

                    {/* Live points preview when at least one field has a points multiplier */}
                    {fields.some((f) => Number.isFinite(Number(f.points)) && Number(f.points) !== 0) ? (
                        <div style={{
                            marginTop: 10, padding: '10px 12px', borderRadius: 8,
                            background: 'rgba(34,197,94,0.10)', color: 'var(--accent-green, #16a34a)',
                            fontSize: 13, fontWeight: 600, textAlign: 'right',
                        }}>
                            Vei câștiga: {fieldStates.reduce((sum, s) => {
                                const v = Number(values[s.field.key])
                                if (!Number.isFinite(v)) return sum
                                const pts = Number(s.field.points)
                                return sum + v * (Number.isFinite(pts) ? pts : 1)
                            }, 0).toFixed(0)} puncte
                        </div>
                    ) : null}

                    {!allValid ? (
                        <div className="cd-kpi-modal-error">
                            {t('card.kpiModal.missingRequired')}
                        </div>
                    ) : null}
                </div>

                <div className="modal-footer">
                    <button type="button" className="btn btn-secondary" onClick={onClose} disabled={saving}>
                        {t('common.cancel')}
                    </button>
                    <button
                        type="button"
                        className="btn btn-primary"
                        disabled={saving || !allValid}
                        onClick={() => {
                            // Pass numeric-coerced values so downstream stores numbers,
                            // not strings.
                            const out = {}
                            for (const s of fieldStates) out[s.field.key] = String(s.n)
                            onSubmit(out)
                        }}
                    >
                        {saving ? t('card.kpiModal.saving') : t('card.kpiModal.saveAndComplete')}
                    </button>
                </div>
            </div>
        </div>
    )
}
